import {
  BIndexThresholdType,
  QBankStructureStatus,
  QuestionStatus,
  useGetAllQBankStructuresQuery,
} from '@report/graphql';
import { Form, Flex, Divider, Button } from '@shared/components';
import { zodResolver } from '@shared/utils';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

const QuestionStatusOptions = [
  { label: 'Published', value: QuestionStatus.Published },
  { label: 'Disabled', value: QuestionStatus.Disabled },
];

import {
  ItemAnalysisDateFilterFormField,
  ItemAnalysisOtherFilterFormField,
} from '../../components';
import { useItemAnalysisSearchParams } from '../../hooks';

import { type ItemAnalysisFilterFormInput, itemAnalysisFilterFormSchema } from './schema';

export default function ItemAnalysisFilterSection() {
  const [
    {
      page,
      pageSize,
      startDate,
      endDate,
      examCourse,
      questionStatus,
      minExaminees,
      knowledgeGroupSelection,
      knowledgeGroupCustomThreshold,
      nonKnowledgeGroupSelection,
      nonKnowledgeGroupCustomThreshold,
    },
    { changeFilterParams },
  ] = useItemAnalysisSearchParams();
  const { data: qBankStructuresData, loading: getAllQBankStructuresLoading } =
    useGetAllQBankStructuresQuery({
      variables: {
        getAllQBankStructuresInput: {
          statuses: [
            QBankStructureStatus.Published,
            QBankStructureStatus.Unpublished,
            QBankStructureStatus.Archived,
          ],
        },
      },
    });

  const qBankStructuresOptions = useMemo(
    () =>
      qBankStructuresData?.reportGetAllQBankStructures.qBankStructures.map((structure) => ({
        label: structure.name,
        value: structure.id,
      })) ?? [],
    [qBankStructuresData],
  );

  const formContext = useForm<ItemAnalysisFilterFormInput>({
    defaultValues: {
      startDate: startDate ?? undefined,
      endDate: endDate ?? undefined,
      questionStatus: questionStatus.map((status) => ({
        label: QuestionStatusOptions.find((option) => option.value === status)?.label ?? '',
        value: status,
      })),
      minExaminees: minExaminees ?? undefined,
      bIndex: {
        knowledgeGroup: {
          knowledgeGroupSelection: knowledgeGroupSelection ?? BIndexThresholdType.UseExamResult,
          customThreshold: knowledgeGroupCustomThreshold ?? undefined,
        },
        nonKnowledgeGroup: {
          nonKnowledgeGroupSelection:
            nonKnowledgeGroupSelection ?? BIndexThresholdType.UseExamResult,
          customThreshold: nonKnowledgeGroupCustomThreshold ?? undefined,
        },
      },
    },
    resolver: zodResolver(itemAnalysisFilterFormSchema),
  });

  const { watch, resetField } = formContext;

  useEffect(() => {
    formContext.reset({
      ...formContext.getValues(),
      examCourse: examCourse
        ? {
            label:
              qBankStructuresOptions.find((option) => option.value === examCourse)?.label ?? '',
            value: examCourse,
          }
        : undefined,
    });
  }, [examCourse, formContext, qBankStructuresOptions]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'bIndex.knowledgeGroup.knowledgeGroupSelection') {
        resetField('bIndex.knowledgeGroup.customThreshold', {
          defaultValue: undefined,
        });
      }
      if (name === 'bIndex.nonKnowledgeGroup.nonKnowledgeGroupSelection') {
        resetField('bIndex.nonKnowledgeGroup.customThreshold', {
          defaultValue: undefined,
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, resetField]);

  const { bIndex } = watch();

  const disableKnowledgeGroupCustomThreshold =
    bIndex.knowledgeGroup.knowledgeGroupSelection !== BIndexThresholdType.CustomThreshold;
  const disableNonKnowledgeGroupCustomThreshold =
    bIndex.nonKnowledgeGroup.nonKnowledgeGroupSelection !== BIndexThresholdType.CustomThreshold;

  const onSubmit = (data: ItemAnalysisFilterFormInput) => {
    const { startDate, endDate, examCourse, questionStatus, minExaminees, bIndex } = data;
    changeFilterParams({
      startDate,
      endDate,
      examCourse: examCourse.value ?? '',
      questionStatus: questionStatus.map((item) => {
        return item.value;
      }),
      minExaminees,
      knowledgeGroupSelection: bIndex.knowledgeGroup.knowledgeGroupSelection,
      knowledgeGroupCustomThreshold:
        bIndex.knowledgeGroup.knowledgeGroupSelection === BIndexThresholdType.CustomThreshold
          ? (bIndex.knowledgeGroup.customThreshold ?? 0)
          : null,
      nonKnowledgeGroupSelection: bIndex.nonKnowledgeGroup.nonKnowledgeGroupSelection,
      nonKnowledgeGroupCustomThreshold:
        bIndex.nonKnowledgeGroup.nonKnowledgeGroupSelection === BIndexThresholdType.CustomThreshold
          ? (bIndex.nonKnowledgeGroup.customThreshold ?? 0)
          : null,
      page: page,
      pageSize: pageSize,
    });
  };

  const handleReset = () => {
    formContext.reset({
      startDate: undefined,
      endDate: undefined,
      examCourse: undefined,
      questionStatus: [],
      minExaminees: undefined,
      bIndex: {
        knowledgeGroup: {
          knowledgeGroupSelection: BIndexThresholdType.UseExamResult,
          customThreshold: undefined,
        },
        nonKnowledgeGroup: {
          nonKnowledgeGroupSelection: BIndexThresholdType.UseExamResult,
          customThreshold: undefined,
        },
      },
    });
    changeFilterParams({
      startDate: null,
      endDate: null,
      examCourse: '',
      questionStatus: [],
      minExaminees: null,
      knowledgeGroupSelection: BIndexThresholdType.UseExamResult,
      knowledgeGroupCustomThreshold: null,
      nonKnowledgeGroupSelection: BIndexThresholdType.UseExamResult,
      nonKnowledgeGroupCustomThreshold: null,
      page: 0,
      pageSize: 50,
    });
  };

  return (
    <FormProvider {...formContext}>
      <Form
        onSubmit={formContext.handleSubmit(onSubmit)}
        sx={(theme) => ({
          p: theme.spacing(4),
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(4),
          border: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: theme.shape.rounded(2),
        })}
      >
        <ItemAnalysisDateFilterFormField />
        <ItemAnalysisOtherFilterFormField
          questionStatusOptions={QuestionStatusOptions}
          disableKnowledgeGroupCustomThreshold={disableKnowledgeGroupCustomThreshold}
          disableNonKnowledgeGroupCustomThreshold={disableNonKnowledgeGroupCustomThreshold}
          qBankStructuresOptions={qBankStructuresOptions}
          questionStatusSelected={formContext.getValues('questionStatus').map((item) => item.value)}
          getAllQBankStructuresLoading={getAllQBankStructuresLoading}
          showKnowledgeGroupThresholdError={
            !!formContext.formState.errors?.bIndex?.knowledgeGroup?.customThreshold
          }
          showNonKnowledgeGroupThresholdError={
            !!formContext.formState.errors?.bIndex?.nonKnowledgeGroup?.customThreshold
          }
        />
        <Divider />
        <Flex sx={{ gap: (theme) => theme.spacing(2), justifyContent: 'end' }}>
          <Button color='secondary' variant='outlined' onClick={() => handleReset()}>
            Reset
          </Button>
          <Button type='submit'>Apply</Button>
        </Flex>
      </Form>
    </FormProvider>
  );
}
